import UiButton from '@components/ui/button/UiButton';
import StateEnum from '@constants/state.enum';
import useNotification from '@hooks/notification/useNotification';
import { PositionElement, PositionsElement } from '@models/positions';
import { getAllPositions } from '@services/positions/positions-http.service';
import { Spin } from 'antd';
import router from 'next/router';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useCallback, useEffect, useState } from 'react';
import PlusIcon from '@assets/svgs/plus-icon-black.svg';
import MinusIcon from '@assets/svgs/minus-icon-black.svg';
import EmptyStateicon from '@assets/svgs/empty-state-positions.svg';
import UploadCvIcon from '@assets/svgs/upload-icon-black.svg';
import PositionCard from './position-card/PositionCard';
import styles from './Positions.module.scss';

type PositionsProps = {
  slug: string;
  clientColor: string;
};

const Positions: React.FC<PositionsProps> = ({ slug, clientColor }) => {
  const { t: translate, i18n } = useTranslation('common');
  const [positions, setPositions] = useState<PositionsElement>([]);
  const [positionSize, setPositionSize] = useState<number>(5);
  const [positionLoading, setPositionLoading] = useState<boolean>(true);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const fetchPositions = useCallback(async () => {
    try {
      setPositionLoading(true);
      let positionsElement: PositionsElement = [];
      positionsElement = await getAllPositions(slug, i18n.language);
      setPositions(positionsElement);
      setPositionLoading(false);
    } catch (e: any) {
      generalError();
      setPositionLoading(true);
    }
  }, [generalError, i18n.language, slug, positionSize]);

  const positionMoreFunc = async () => {
    setPositionSize(positionSize + 5);
  };

  const positionLessFunc = async () => {
    setPositionSize(positionSize - 5);
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  const getPositionsCards = () => {
    if (positions.length > 0) {
      return positions.slice(0, positionSize).map((position: PositionElement) => {
        return (
          <Link key={position.id} href={`/${slug}/positions/${position.id}`} passHref>
            <div key={position.id} className={styles.cardWrap}>
              <PositionCard position={position} />
            </div>
          </Link>
        );
      });
    }
    return (
      <div className={styles.emptyState}>
        <EmptyStateicon className={styles.emptyIcon} />
        <h4 className={styles.emptyTitle}>{translate('no_openings')}</h4>
        <p className={styles.emptyDescription}>{translate('empty_state_positions_text')}</p>
        <div style={{ backgroundColor: clientColor || '#ff2458', borderRadius: '8px' }}>
          <UiButton
            onClick={() => router.push(`#general-cv-section`)}
            type="default"
            size="small"
            htmlType="submit"
            icon={<UploadCvIcon fill={clientColor || '#ff2458'} />}
            style={{ color: clientColor || '#ff2458', borderColor: clientColor || '#ff2458' }}
            className={styles.cvButton}
          >
            {translate('cv')}
          </UiButton>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.positionsWrap} id="positions-section">
      <h1 className={styles.title} style={{ color: clientColor }}>
        {translate('openings')}
      </h1>
      {positionLoading ? <Spin size="large" /> : getPositionsCards()}
      {positions.length > 0 && (
        <h5 className={styles.positionsCounter}>
          {positionSize >= positions.length
            ? translate('position_counter', { size: positions.length, length: positions.length })
            : translate('position_counter', { size: positionSize, length: positions.length })}
        </h5>
      )}
      <div className={styles.moreLessButtons}>
        {positionSize <= positions.length && (
          <div style={{ marginTop: '26px', backgroundColor: clientColor || '#ff2458', borderRadius: '8px' }}>
            <UiButton
              type="default"
              size="small"
              htmlType="submit"
              icon={<PlusIcon stroke={clientColor || '#ff2458'} />}
              onClick={() => positionMoreFunc()}
              className={styles.showMoreButton}
              style={{ color: clientColor || '#ff2458', borderColor: clientColor || '#ff2458' }}
            >
              {translate('show_more')}
            </UiButton>
          </div>
        )}
        {positionSize > 5 && (
          <UiButton
            type="link"
            size="small"
            htmlType="submit"
            icon={<MinusIcon stroke={clientColor} />}
            onClick={() => positionLessFunc()}
            className={styles.showButton}
            style={{ color: clientColor }}
          >
            {translate('show_less')}
          </UiButton>
        )}
      </div>
    </div>
  );
};

export default Positions;
